// =====================
// Script to show only the selected Variant Images of the Product Page
// =====================
// Version V12.1 - mobile slide filtering only; preserves desktop behavior
// Updated by RCH/ChatGPT 2026-04-13

(() => {
  const PRODUCT_JSON_ID = "HZN_ProductJSON";
  const DEBUG = false;
  const log = (...args) => DEBUG && console.log("[HZN Variant Images v12.1]", ...args);

  const HIDE_CLASS = "hzn-evi-hidden";
  const STYLE_ID = "hzn-evi-style";
  const MOBILE_MQ = window.matchMedia("(max-width: 749px)");

  function ensureHideStyle() {
    if (document.getElementById(STYLE_ID)) return;
    const s = document.createElement("style");
    s.id = STYLE_ID;
    s.textContent = `
      .${HIDE_CLASS} { display: none !important; }

      /* Only hide actual slideshow slides on mobile */
      @media screen and (max-width: 749px) {
        slideshow-slide.${HIDE_CLASS},
        slideshow-slide[hidden] {
          display: none !important;
        }
      }
    `;
    document.head.appendChild(s);
  }

  function isMobileView() {
    return MOBILE_MQ.matches;
  }

  function normalizeId(id) {
    if (id == null) return null;
    const m = String(id).match(/(\d+)(?!.*\d)/);
    return m ? Number(m[1]) : null;
  }

  function getProductData() {
    const el = document.getElementById(PRODUCT_JSON_ID);
    if (!el) return null;
    try { return JSON.parse(el.textContent || "{}"); } catch { return null; }
  }

  function getSelectedVariantId() {
    const idInput =
      document.querySelector('form[action*="/cart/add"] input[name="id"]') ||
      document.querySelector('input[name="id"]');
    return idInput?.value ? Number(idInput.value) : null;
  }

  // Desktop only — the mobile:hidden slideshow-controls
  function getDesktopSlideshowControls() {
    return Array.from(document.querySelectorAll("slideshow-controls"))
      .find(el => el.classList.contains("mobile:hidden")) || null;
  }

  // Desktop thumbnail buttons only
  function getThumbnailButtons() {
    const desktop = getDesktopSlideshowControls();
    if (!desktop) return [];
    return Array.from(desktop.querySelectorAll('button[on\\:click^="/select/"]'));
  }

  function getThumbIndex(btn) {
    const v = btn.getAttribute("on:click");
    if (!v) return null;
    const m = v.match(/\/select\/(\d+)/);
    return m ? Number(m[1]) : null;
  }

  function fileKeyFromUrl(url) {
    if (!url) return null;
    const u = String(url).split("?")[0];
    const m = u.match(/\/files\/([^\/]+)$/i);
    if (!m) return null;
    return decodeURIComponent(m[1]).toLowerCase();
  }

  function fileKeyFromMedia(m) {
    const candidates = [
      m?.preview_image?.src,
      m?.preview_image?.url,
      m?.image?.src,
      m?.image?.url,
      m?.src,
      m?.url,
    ].filter(Boolean);

    for (const c of candidates) {
      const key = fileKeyFromUrl(c);
      if (key) return key;
    }
    return null;
  }

  function buildThumbIndexToMediaIdMap(product, buttons) {
    const fileToMediaId = new Map();
    const mediaArr = Array.isArray(product?.media) ? product.media : [];

    for (const m of mediaArr) {
      const mid = normalizeId(m?.id);
      if (!mid) continue;
      const key = fileKeyFromMedia(m);
      if (!key) continue;
      if (!fileToMediaId.has(key)) fileToMediaId.set(key, mid);
    }

    const thumbIndexToMediaId = new Map();

    for (const btn of buttons) {
      const idx = getThumbIndex(btn);
      if (idx == null) continue;

      const img = btn.querySelector("img");
      const key = fileKeyFromUrl(img?.getAttribute("src") || img?.src);
      if (!key) continue;

      const mid = fileToMediaId.get(key);
      if (!mid) continue;

      thumbIndexToMediaId.set(idx, mid);
    }

    return { thumbIndexToMediaId };
  }

  function buildGrouping(product) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const media = Array.isArray(product?.media) ? product.media : [];
    const mediaOrder = media.map(m => normalizeId(m?.id)).filter(Boolean);

    const variantAnchorByVariantId = new Map();
    const anchorSet = new Set();

    for (const v of variants) {
      const vid = Number(v.id);
      const mid =
        normalizeId(v?.featured_media?.id) ||
        normalizeId(v?.featured_image?.id);

      if (vid && mid) {
        variantAnchorByVariantId.set(vid, mid);
        anchorSet.add(mid);
      }
    }

    const firstMediaId = mediaOrder[0] || null;
    const defaultOnlyMediaId = (firstMediaId && !anchorSet.has(firstMediaId)) ? firstMediaId : null;

    const globalMedia = [];
    let firstAnchorSeen = false;
    const groupByAnchor = new Map();
    let currentAnchor = null;

    for (const mid of mediaOrder) {
      const isAnchor = anchorSet.has(mid);

      if (isAnchor) {
        firstAnchorSeen = true;
        currentAnchor = mid;
        if (!groupByAnchor.has(currentAnchor)) groupByAnchor.set(currentAnchor, []);
        groupByAnchor.get(currentAnchor).push(mid);
        continue;
      }

      if (!firstAnchorSeen) {
        if (!(defaultOnlyMediaId && mid === defaultOnlyMediaId)) globalMedia.push(mid);
        continue;
      }

      if (currentAnchor) groupByAnchor.get(currentAnchor)?.push(mid);
    }

    return { variantAnchorByVariantId, groupByAnchor, defaultOnlyMediaId, globalMedia };
  }

  function getAllowedMediaIds(grouping, variantId, hasInteracted) {
    const anchorMediaId = variantId ? grouping.variantAnchorByVariantId.get(variantId) : null;
    if (!anchorMediaId) return [];

    let allowedMediaIds = [];
    if (grouping.groupByAnchor.has(anchorMediaId)) {
      allowedMediaIds = [...grouping.globalMedia, ...grouping.groupByAnchor.get(anchorMediaId)];
    } else {
      allowedMediaIds = [anchorMediaId];
    }

    if (!hasInteracted && grouping.defaultOnlyMediaId) {
      return [grouping.defaultOnlyMediaId];
    }

    if (hasInteracted && grouping.defaultOnlyMediaId) {
      allowedMediaIds = allowedMediaIds.filter(id => id !== grouping.defaultOnlyMediaId);
    }

    return allowedMediaIds;
  }

  function getAllSlides() {
    return Array.from(document.querySelectorAll("media-gallery slideshow-slide"));
  }

  function getVisibleSlides() {
    return getAllSlides().filter(slide => !slide.hidden && !slide.classList.contains(HIDE_CLASS));
  }

  function getActiveSlideIndex() {
    const slides = getAllSlides();
    return slides.findIndex(slide => slide.getAttribute("aria-hidden") === "false");
  }

  function getMobileCounter() {
    return document.querySelector(".media-gallery__mobile-controls .slideshow-controls__counter");
  }

  function updateMobileCounter() {
    if (!isMobileView()) return;

    const counter = getMobileCounter();
    if (!counter) return;

    const allSlides = getAllSlides();
    const visibleSlides = getVisibleSlides();
    if (!visibleSlides.length) return;

    const activeIndex = getActiveSlideIndex();
    const activeSlide = activeIndex >= 0 ? allSlides[activeIndex] : null;

    let visiblePosition = 1;
    if (activeSlide) {
      const idx = visibleSlides.indexOf(activeSlide);
      if (idx >= 0) visiblePosition = idx + 1;
    }

    counter.innerHTML = `<span ref="current">${visiblePosition}</span><span class="slash">/</span>${visibleSlides.length}`;
  }

  function jumpToThumbIndex(targetIndex) {
    const buttons = getThumbnailButtons();
    const target = buttons.find(btn => getThumbIndex(btn) === targetIndex);
    if (target) target.click();
  }

  function syncSlidesToAllowedMedia(product, allowedSet) {
    const slides = getAllSlides();
    if (!slides.length) return;

    // On desktop, DO NOT hide actual slides. That was the regression.
    if (!isMobileView()) {
      slides.forEach(slide => {
        slide.hidden = false;
        slide.classList.remove(HIDE_CLASS);
      });
      return;
    }

    const buttons = getThumbnailButtons();
    const { thumbIndexToMediaId } = buildThumbIndexToMediaIdMap(product, buttons);

    slides.forEach((slide, idx) => {
      const mid = thumbIndexToMediaId.get(idx);

      // If we cannot map the slide, leave it visible.
      if (!mid) {
        slide.hidden = false;
        slide.classList.remove(HIDE_CLASS);
        return;
      }

      if (allowedSet.has(mid)) {
        slide.hidden = false;
        slide.classList.remove(HIDE_CLASS);
      } else {
        slide.hidden = true;
        slide.classList.add(HIDE_CLASS);
      }
    });

    const activeIndex = getActiveSlideIndex();
    const activeSlide = activeIndex >= 0 ? slides[activeIndex] : null;
    const activeIsVisible = activeSlide && !activeSlide.hidden && !activeSlide.classList.contains(HIDE_CLASS);

    if (!activeIsVisible) {
      const firstVisibleIndex = slides.findIndex(slide => !slide.hidden && !slide.classList.contains(HIDE_CLASS));
      if (firstVisibleIndex >= 0) {
        jumpToThumbIndex(firstVisibleIndex);
      }
    }

    updateMobileCounter();
  }

  // =====================================================================
  // Chevron patch — desktop only
  // =====================================================================
  function patchChevronNavigation() {
    function getDesktopArrows() {
      return Array.from(document.querySelectorAll("slideshow-arrows"))
        .find(el => el.classList.contains("mobile:hidden")) || null;
    }

    function getVisibleThumbs() {
      return getThumbnailButtons().filter(btn => !btn.classList.contains(HIDE_CLASS));
    }

    function getAllThumbs() {
      return getThumbnailButtons();
    }

    function correctAfterHorizonAdvance(direction) {
      let corrected = false;

      [50, 150, 300].forEach(delay => {
        setTimeout(() => {
          if (corrected) return;

          const allThumbs = getAllThumbs();
          const visibleThumbs = getVisibleThumbs();

          if (!allThumbs.length || !visibleThumbs.length) return;

          const horizonSelectedIdx = allThumbs.findIndex(
            btn => btn.getAttribute("aria-selected") === "true"
          );

          if (horizonSelectedIdx === -1) return;

          const horizonBtn = allThumbs[horizonSelectedIdx];
          const isVisible = !horizonBtn.classList.contains(HIDE_CLASS);

          if (isVisible) {
            corrected = true;
            return;
          }

          let targetBtn = null;

          if (direction === "next") {
            targetBtn = allThumbs.slice(horizonSelectedIdx + 1).find(
              btn => !btn.classList.contains(HIDE_CLASS)
            );
            if (!targetBtn) targetBtn = visibleThumbs[0];
          } else {
            targetBtn = allThumbs.slice(0, horizonSelectedIdx).reverse().find(
              btn => !btn.classList.contains(HIDE_CLASS)
            );
            if (!targetBtn) targetBtn = visibleThumbs[visibleThumbs.length - 1];
          }

          if (targetBtn) {
            corrected = true;
            targetBtn.click();
          }
        }, delay);
      });
    }

    function handlePrev() {
      if (!isMobileView()) correctAfterHorizonAdvance("prev");
    }

    function handleNext() {
      if (!isMobileView()) correctAfterHorizonAdvance("next");
    }

    function attachChevronListeners() {
      const arrows = getDesktopArrows();
      if (!arrows) return;

      const prevBtn = arrows.querySelector('button[on\\:click="/previous"]');
      const nextBtn = arrows.querySelector('button[on\\:click="/next"]');

      if (prevBtn && !prevBtn.dataset.capPatched) {
        prevBtn.dataset.capPatched = "1";
        prevBtn.addEventListener("click", handlePrev, true);
      }

      if (nextBtn && !nextBtn.dataset.capPatched) {
        nextBtn.dataset.capPatched = "1";
        nextBtn.addEventListener("click", handleNext, true);
      }
    }

    attachChevronListeners();

    const observer = new MutationObserver(() => attachChevronListeners());
    observer.observe(document.body, { childList: true, subtree: true });
  }
  // =====================================================================

  function main() {
    ensureHideStyle();

    const product = getProductData();
    if (!product) return;

    const grouping = buildGrouping(product);
    let hasInteracted = false;

    function applyFilter(reason = "apply") {
      const buttons = getThumbnailButtons();
      if (!buttons.length) return;

      const { thumbIndexToMediaId } = buildThumbIndexToMediaIdMap(product, buttons);
      const variantId = getSelectedVariantId();
      const allowedMediaIds = getAllowedMediaIds(grouping, variantId, hasInteracted);
      const allowedSet = new Set(allowedMediaIds);

      const anchorMediaId = variantId ? grouping.variantAnchorByVariantId.get(variantId) : null;
      if (!anchorMediaId && !allowedMediaIds.length) return;

      // Desktop thumbnail filtering (same idea as your working version)
      for (const btn of buttons) {
        const idx = getThumbIndex(btn);
        const mid = idx != null ? thumbIndexToMediaId.get(idx) : null;

        if (!mid) {
          btn.classList.remove(HIDE_CLASS);
          continue;
        }

        if (allowedSet.has(mid)) btn.classList.remove(HIDE_CLASS);
        else btn.classList.add(HIDE_CLASS);
      }

      // Mobile slideshow filtering only
      syncSlidesToAllowedMedia(product, allowedSet);

      log(reason, {
        variantId,
        allowedCount: allowedMediaIds.length,
        buttons: buttons.length,
        slides: getAllSlides().length,
        mobile: isMobileView()
      });
    }

    setTimeout(() => applyFilter("load"), 0);

    function onVariantChange() {
      if (!hasInteracted) hasInteracted = true;

      applyFilter("variant-change t0");
      setTimeout(() => applyFilter("variant-change t120"), 120);
      setTimeout(() => applyFilter("variant-change t350"), 350);
      setTimeout(() => applyFilter("variant-change t700"), 700);
    }

    document.addEventListener("change", (e) => {
      const t = e.target;
      if (!t) return;

      if (
        t.closest("variant-picker") ||
        t.matches("select, input[type='radio'], input[type='checkbox']")
      ) {
        onVariantChange();
      }
    }, true);

    document.addEventListener("variant:change", onVariantChange);
    document.addEventListener("product:variant-change", onVariantChange);

    // Keep mobile counter in sync
    const slideRoot = document.querySelector("media-gallery") || document.querySelector("main") || document.documentElement;
    let slideCounterTimer = null;
    const slideObs = new MutationObserver(() => {
      clearTimeout(slideCounterTimer);
      slideCounterTimer = setTimeout(() => updateMobileCounter(), 50);
    });
    slideObs.observe(slideRoot, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["aria-hidden", "hidden", "class"]
    });

    const root = getDesktopSlideshowControls() || document.querySelector("main") || document.documentElement;
    let timer = null;
    const obs = new MutationObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(() => applyFilter("mutation"), 200);
    });
    obs.observe(root, { childList: true, subtree: true });

    // Re-apply when viewport crosses mobile/desktop boundary
    if (typeof MOBILE_MQ.addEventListener === "function") {
      MOBILE_MQ.addEventListener("change", () => setTimeout(() => applyFilter("viewport-change"), 50));
    } else if (typeof MOBILE_MQ.addListener === "function") {
      MOBILE_MQ.addListener(() => setTimeout(() => applyFilter("viewport-change"), 50));
    }

    setTimeout(patchChevronNavigation, 300);
    setTimeout(updateMobileCounter, 400);
  }

  document.addEventListener("DOMContentLoaded", main);
})();